import { HttpErrorResponse } from '@angular/common/http'
import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router'

import { Observable, map } from 'rxjs'

import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'

import { whenLoaded } from './when-loaded'

import { AssetPermissionValue } from '../entities/application.entity'
import { ToastMessageService } from '../services/toast-message.service'

const PROJECT_NO_ACCESS_MESSAGE = 'You do not have access to this project, or the link is not valid'

/**
 * Waits for the project's permissions to load. A load that failed leaves no permissions, so it
 * counts as not permitted too: the page would break without them.
 */
export function DefaultApplicationPermissionsGuard(
  permission: AssetPermissionValue
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router)
    const applicationPermissionsStore = inject(ApplicationPermissionsStore)

    return whenLoaded(applicationPermissionsStore.applicationPermissions.isLoading).pipe(
      map(() => {
        const isPermitted = applicationPermissionsStore.permissions()?.[permission]
        return isPermitted ? true : router.parseUrl('/403')
      })
    )
  }
}

type ProjectAccess = 'granted' | 'denied' | 'failed'

/** What the project's loaded permissions say about opening it. */
const projectAccess = (
  permissions: Record<AssetPermissionValue, boolean> | null,
  error: Error | undefined
): ProjectAccess => {
  if (permissions?.project_read) {
    return 'granted'
  }

  // Answered without `project_read`: the user can't open the project.
  if (error === undefined) {
    return 'denied'
  }

  // 400 is how the endpoint answers for a project that isn't in the workspace.
  if (error instanceof HttpErrorResponse && error.status === 400) {
    return 'denied'
  }

  // Any other failure says nothing about access.
  return 'failed'
}

/** Checks the project before any page inside it, once its permissions have loaded. */
export const projectAccessGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<true | UrlTree> => {
  const router = inject(Router)
  const toastMessageService = inject(ToastMessageService)
  const applicationPermissionsStore = inject(ApplicationPermissionsStore)
  const workspaceId = route.paramMap.get('workspaceId')

  return whenLoaded(applicationPermissionsStore.applicationPermissions.isLoading).pipe(
    map(() => {
      const access = projectAccess(
        applicationPermissionsStore.permissions(),
        applicationPermissionsStore.applicationPermissions.error()
      )

      switch (access) {
        case 'granted':
          return true
        case 'denied':
          toastMessageService.showErrorMessage(PROJECT_NO_ACCESS_MESSAGE)
          return router.createUrlTree(['/guard', 'workspace', workspaceId, 'projects'])
        case 'failed':
          return router.parseUrl('/403')
      }
    })
  )
}
